import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { GuestPassesQueryKey } from "@/hooks/useHomeData";
import { fetchUserGuestPasses } from "@/services/finance";

import { getDaysTillExpiry, GuestPass } from ".";
import { GuestPassShareModal } from "./GuestPassShareModal";

export type GuestPassFaq = {
  question: string;
  answer: string;
};

type Props = {
  passes: GuestPass[];
};

type CardsForView = {
  focus: GuestPass;
  other: GuestPass[];
};

const otherCardColors = [
  { bg: "bg-clear-sky", text: "text-yet-another-blue" },
  { bg: "bg-studer-blue", text: "text-linx-white" },
  { bg: "bg-mute-grey", text: "text-lighter-grey" },
];

const cardDropShadowStyle = "rgba(0, 0, 0, .2) 0px 4px 6px 0px";

export default function GuestPassGroupCard({ passes }: Props) {
  const guestPasses = useQuery({
    queryKey: GuestPassesQueryKey,
    queryFn: fetchUserGuestPasses,
  });

  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [passToShare, setPassToShare] = useState<GuestPass | null>(null);
  const [cardNameInFocus, setCardNameInFocus] = useState<string | null>(null);

  useEffect(() => {
    const cardNameInFocus = passes[passes.length - 1]?.name;
    setCardNameInFocus(cardNameInFocus);
  }, [passes]);

  const cards = useMemo((): CardsForView => {
    const focusFilter = passes.filter((p) => p.name === cardNameInFocus);
    const focus =
      focusFilter.length === 1 ? focusFilter[0] : passes[passes.length - 1];
    return {
      focus,
      other: focus
        ? passes.filter((p) => p.name !== cardNameInFocus)
        : passes.slice(1),
    };
  }, [passes, cardNameInFocus]);

  const sharePass = () => {
    setShowShareModal(true);
    setPassToShare(cards.focus);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setPassToShare(null);
  };

  if (!passes.length) {
    return null;
  }

  const expiresInDays = getDaysTillExpiry(cards.focus.expiresDate);

  return (
    <div
      style={{
        height: `${(passes.length - 1) * 70 + 300}px`,
      }}
    >
      <div
        data-testid="focusGuessPassCard"
        className={
          "relative flex w-full flex-col items-center justify-start gap-8 self-stretch rounded-xl bg-deep-blue p-4 text-clear-sky"
        }
        style={{
          zIndex: passes.length + 1,
          boxShadow: cardDropShadowStyle,
        }}
      >
        <div className="flex flex-col items-center justify-start gap-1 self-stretch">
          <div className="flex flex-col items-center justify-start gap-4 self-stretch pb-1">
            <div className="flex flex-col items-start justify-start gap-2 self-stretch">
              <div className="flex flex-col items-center justify-start gap-[5px] self-stretch">
                <div className="mb-2 mt-1 justify-start self-stretch text-center font-['Poppins'] text-xl font-semibold leading-7">
                  {cards.focus.name}
                </div>
              </div>
            </div>
          </div>
          <div className="h-4 justify-end self-stretch px-4 text-center font-['Open_Sans'] text-xs font-normal leading-none">
            {cards.focus.subText}
          </div>
        </div>
        <div className="flex flex-col items-center justify-start gap-0.5 self-stretch">
          <div className="size-16 rounded-full bg-clear-sky" />
          <div
            data-testid="focusGuessPassCount"
            className="absolute mt-5 size-16 justify-center text-center font-['Poppins'] text-5xl font-semibold leading-7 text-deep-blue"
          >
            {cards.focus.count}
          </div>
          <div className="inline-flex h-5 items-center justify-center self-stretch">
            <div className="w-80 justify-end text-center font-['Open_Sans'] text-sm font-semibold leading-loose">
              Passes Remaining
            </div>
          </div>
        </div>
        <div className="inline-flex w-full items-center justify-between">
          <div className="justify-end font-['Open_Sans'] text-sm leading-none">
            {expiresInDays && (
              <>
                <span className="gap-4 font-normal">Expires in&nbsp;</span>
                <span className="font-semibold">{expiresInDays}</span>
              </>
            )}
          </div>
          <div className="flex h-8 items-center justify-center gap-2 rounded-[10px] bg-teal-700 px-3 py-2">
            <button
              onClick={sharePass}
              className="justify-start whitespace-pre font-['Open_Sans'] text-sm font-semibold leading-loose text-slate-100"
            >
              Share now
            </button>
          </div>
        </div>
      </div>
      <div className="relative">
        {cards.other.map((p, i) => {
          const colors = otherCardColors[i % otherCardColors.length];
          const expiresInDays = getDaysTillExpiry(p.expiresDate);
          const top = i === 0 ? `-14px` : `${i * 64 - 14}px`;
          return (
            <div
              data-testid={`stackedGuestPassCard-${i}`}
              onClick={() => setCardNameInFocus(p.name)}
              key={p.name}
              className={twMerge(
                "absolute flex w-full flex-col items-center justify-start gap-8 self-stretch rounded-b-xl p-4 pt-8",
                colors.text,
                colors.bg,
              )}
              style={{
                zIndex: passes.length - i,
                top,
                boxShadow: cardDropShadowStyle,
              }}
            >
              <div className="flex flex-col items-center justify-start gap-1 self-stretch">
                <div className="flex flex-col items-center justify-start gap-4 self-stretch pb-1">
                  <div className="flex flex-col items-start justify-start gap-2 self-stretch">
                    <div className="mt-1 flex flex-row justify-between gap-[5px] self-stretch font-['Poppins']">
                      <div className="justify-start text-sm font-semibold">
                        {`${p.name} Passes`}
                      </div>
                      {expiresInDays && (
                        <div
                          className="text-xs font-normal"
                          style={{ lineHeight: 2 }}
                        >
                          <span>Expires in&nbsp;</span>
                          <span className="font-semibold">{expiresInDays}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {showShareModal && passToShare && (
        <GuestPassShareModal
          isOpen={showShareModal}
          setIsOpen={closeShareModal}
          pass={passToShare}
          shareSuccess={guestPasses.refetch}
        />
      )}
    </div>
  );
}
